/**
 * useFollowStatus — batches multiple follow-status lookups into a single
 * POST /api/users/follow-status/batch request.
 *
 * Motivation: the Feed rendered ~100 <FollowButton /> at once, each firing
 * an individual `/users/{id}/follow-status` request. That storm was starving
 * the Kubernetes ingress and freezing navigation.
 *
 * Behaviour:
 *  - Each hook call registers its userId into a shared queue.
 *  - The queue is flushed once per animation frame (~16 ms), so N mounts in
 *    the same render tick collapse into ONE network request.
 *  - Results are cached in-memory. Consumers can call `refresh(userId)` to
 *    invalidate a single entry after a follow/unfollow mutation.
 *  - `useFollowStatus(null)` is a no-op.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cache = new Map(); // user_id → {following, followers_count, following_count}
const pending = new Map(); // user_id → [resolve, ...]
const subscribers = new Map(); // user_id → Set<callback>
let flushScheduled = false;

const scheduleFlush = () => {
  if (flushScheduled) return;
  flushScheduled = true;
  const flush = async () => {
    flushScheduled = false;
    const ids = Array.from(pending.keys());
    if (!ids.length) return;
    const resolversByUser = new Map(pending);
    pending.clear();
    try {
      const r = await axios.post(`${API}/users/follow-status/batch`, { user_ids: ids });
      for (const uid of ids) {
        const data = r.data[uid] || { following: false, followers_count: 0, following_count: 0 };
        cache.set(uid, data);
        (resolversByUser.get(uid) || []).forEach((cb) => cb(data));
        (subscribers.get(uid) || new Set()).forEach((cb) => cb(data));
      }
    } catch {
      for (const uid of ids) {
        (resolversByUser.get(uid) || []).forEach((cb) => cb(null));
      }
    }
  };
  if (typeof window !== "undefined" && window.requestAnimationFrame) {
    window.requestAnimationFrame(flush);
  } else {
    setTimeout(flush, 16);
  }
};

const request = (userId) => {
  if (cache.has(userId)) return Promise.resolve(cache.get(userId));
  return new Promise((resolve) => {
    const arr = pending.get(userId) || [];
    arr.push(resolve);
    pending.set(userId, arr);
    scheduleFlush();
  });
};

export function useFollowStatus(userId, currentUserId) {
  const [status, setStatus] = useState(cache.get(userId) || null);
  const [loading, setLoading] = useState(!cache.has(userId));

  useEffect(() => {
    if (!userId || (currentUserId && userId === currentUserId)) {
      setStatus(null); setLoading(false);
      return;
    }
    let alive = true;
    if (cache.has(userId)) {
      setStatus(cache.get(userId));
      setLoading(false);
    } else {
      setLoading(true);
      request(userId).then((data) => { if (alive && data) { setStatus(data); setLoading(false); } });
    }
    const cb = (data) => { if (alive) setStatus(data); };
    const set = subscribers.get(userId) || new Set();
    set.add(cb);
    subscribers.set(userId, set);
    return () => {
      alive = false;
      const s = subscribers.get(userId);
      if (s) { s.delete(cb); if (!s.size) subscribers.delete(userId); }
    };
  }, [userId, currentUserId]);

  const updateLocal = useCallback((partial) => {
    if (!userId) return;
    const current = cache.get(userId) || { following: false, followers_count: 0, following_count: 0 };
    const next = { ...current, ...partial };
    cache.set(userId, next);
    setStatus(next);
    (subscribers.get(userId) || new Set()).forEach((cb) => cb(next));
  }, [userId]);

  return { status, loading, updateLocal };
}

export function invalidateFollowStatus(userId) {
  cache.delete(userId);
}
